import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getJson, postJson } from '../api/client.ts';
import { OutboxLoginHint } from '../components/outbox-panel.tsx';
import { Badge, Button, Checkbox, ErrorNote, Input, Notice, Spinner } from '../components/ui.tsx';
import { LOGOUT_PENDING_NOTE, usePendingLogout } from '../lib/logout.ts';

/**
 * Anmeldeseite. Hauptweg ist Microsoft Entra; darunter die Anmeldung ohne
 * Microsoft-Konto für die Werkstatt (Benutzername + Passwort, von der
 * Systemverwaltung angelegt). Der Entwicklermodus zeigt zusätzlich eine
 * Rollenauswahl – in Produktion verhindert die Konfiguration ihn.
 */
const LOGIN_ERRORS: Record<string, string> = {
  abgelaufen: 'Die Anmeldung hat zu lange gedauert. Bitte erneut versuchen.',
  fehlgeschlagen: 'Die Anmeldung konnte nicht abgeschlossen werden. Bitte erneut versuchen. Klappt es wieder nicht, bitte an die IT wenden.',
  mandant: 'Anmeldung nur mit einem Konto der Arnold AG möglich.',
  gruppen: 'Ihre Gruppenzugehörigkeit konnte nicht gelesen werden. Bitte an die IT wenden.',
  kein_zugang: 'Für das Instandhaltungs-Helpdesk ist kein Zugang hinterlegt. Bitte an die IT wenden.',
  adresse_vergeben: 'Ihre E-Mail-Adresse gehört hier noch zu einem anderen Konto. Bitte an die Systemverwaltung wenden: Sie deaktiviert das alte Konto, danach klappt die Anmeldung.',
};

/** `onBack`: Aufruf von einer öffentlichen Seite, die ohne eigenen Verlaufseintrag hierher umschaltet. */
export function LoginPage({ onBack }: { onBack?: () => void }) {
  const options = useQuery({
    queryKey: ['auth', 'options'],
    queryFn: () => getJson<{ mode: 'entra' | 'dev'; localLogin: boolean }>('/api/auth/options'),
    retry: false,
  });
  const [local, setLocal] = useState(false);
  const logoutOpen = usePendingLogout();
  const errorCode = new URLSearchParams(window.location.search).get('anmeldung');
  const back = onBack ? (
    <button type="button" className="w-full cursor-pointer text-center text-sm text-[var(--text-muted)] underline" onClick={onBack}>
      Zurück
    </button>
  ) : null;

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-steel-900 px-4 py-12">
      <div className="w-full max-w-[440px]">
        <div className="mb-8 flex flex-col items-center text-center text-white">
          <img src="/arnold-logo-white.svg" alt="Arnold AG" className="h-[34px] w-auto" />
          <span className="my-5 block h-px w-16 bg-steel-700" aria-hidden />
          <h1 className="text-[26px] tracking-[var(--tracking-caps)] text-white">Instandhaltung</h1>
          <p className="mt-2 text-[13px] text-steel-400">Was ist an welcher Maschine los, und wer ist dran?</p>
        </div>
        <div className="ad-card space-y-4 p-7 shadow-[var(--shadow-overlay)]">
          {errorCode && LOGIN_ERRORS[errorCode] ? <ErrorNote error={new Error(LOGIN_ERRORS[errorCode])} /> : null}
          <OutboxLoginHint />
          {/* Vor der neuen Anmeldung muss die alte Sitzung weg sein, sonst beendet das Nachholen die neue. Ohne Netz ginge die Anmeldung ohnehin nicht. */}
          {logoutOpen ? (
            <>
              <Notice tone="warn">{LOGOUT_PENDING_NOTE} Erst danach ist hier eine neue Anmeldung möglich.</Notice>
              {back}
            </>
          ) : options.isPending ? (
            <Spinner label="Anmeldung wird vorbereitet …" />
          ) : local ? (
            <LocalLogin onBack={() => setLocal(false)} />
          ) : (
            <>
              {options.data?.mode === 'dev' ? <DevLogin /> : <EntraLogin />}
              {options.data?.localLogin !== false ? (
                <>
                  <div className="flex items-center gap-3 pt-1 text-[13px] text-[var(--text-muted)] uppercase" aria-hidden>
                    <span className="h-px flex-1 bg-[var(--border-default)]" />
                    oder
                    <span className="h-px flex-1 bg-[var(--border-default)]" />
                  </div>
                  <Button variant="outline" size="lg" className="w-full" onClick={() => setLocal(true)}>
                    Ohne Microsoft-Konto anmelden
                  </Button>
                </>
              ) : null}
              {back}
            </>
          )}
        </div>
        {/* Von der Maschinenseite aus ist der QR-Code längst gescannt, und `LoginNeeded` hat gerade gesagt, warum Melden dort nicht geht. */}
        {onBack ? null : (
          <p className="mt-6 text-center text-[13px] text-steel-400">
            Kein Konto? An der Maschine den QR-Code scannen. Im Firmennetz geht Melden auch ohne Anmeldung.
          </p>
        )}
      </div>
    </div>
  );
}

function EntraLogin() {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search.replace(/[?&]anmeldung=[^&]*/, ''));
  return (
    <>
      <h2 className="text-lg">Anmeldung</h2>
      <p className="text-sm text-[var(--text-muted)]">Die Anmeldung läuft über Ihr Microsoft-Konto der Arnold AG.</p>
      {/* Ein Link, kein fetch: Entra erwartet einen echten Seitenwechsel. */}
      <a className="ad-button-primary ad-button-lg w-full" href={`/api/auth/login?returnTo=${returnTo}`}>
        Mit Microsoft anmelden
      </a>
    </>
  );
}

/** Werkstatt-Konto: Benutzername + Passwort (§ lokale Konten). */
function LocalLogin({ onBack }: { onBack: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await postJson('/api/auth/local-login', { username, password });
      window.location.reload();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h2 className="text-lg">Anmeldung ohne Microsoft-Konto</h2>
      <p className="text-sm text-[var(--text-muted)]">Für die Werkstatt: Benutzername und Passwort bekommen Sie von der Systemverwaltung.</p>
      <Input label="Benutzername" value={username} onChange={setUsername} autoComplete="username" autoFocus placeholder="z. B. max.schlosser" />
      <Input label="Passwort" type="password" value={password} onChange={setPassword} autoComplete="current-password" />
      {error ? <ErrorNote error={error} /> : null}
      <Button type="submit" size="lg" className="w-full" disabled={busy || !username.trim() || !password}>
        {busy ? 'Anmeldung läuft …' : 'Anmelden'}
      </Button>
      <button type="button" className="w-full cursor-pointer text-center text-sm text-[var(--text-muted)] underline" onClick={onBack}>
        Zurück zur Anmeldung mit Microsoft
      </button>
    </form>
  );
}

const ROLES = [
  ['reporter', 'Melder', 'Störungen melden, eigene Meldungen verfolgen'],
  ['technician', 'Instandhalter', 'Warteschlange des Standorts, Tickets bearbeiten'],
  ['lead', 'Instandhaltungsleitung', 'Beide Standorte, Auswertungen, Freigaben'],
] as const;

function DevLogin() {
  const [role, setRole] = useState<(typeof ROLES)[number][0]>('lead');
  const [sysadmin, setSysadmin] = useState(false);
  const [site, setSite] = useState<'FRD' | 'STH' | 'BEIDE'>('FRD');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    const names = { reporter: 'Wera Werker', technician: 'Tom Instandhalter', lead: 'Petra Leitung' } as const;
    try {
      await postJson('/api/auth/dev-login', {
        email: `${role}${sysadmin ? '-admin' : ''}@arnold.test`,
        displayName: `${names[role]}${sysadmin ? ' (Admin)' : ''}`,
        role,
        isSysadmin: sysadmin,
        siteCodes: site === 'BEIDE' ? ['FRD', 'STH'] : [site],
      });
      window.location.reload();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg">Entwickler-Anmeldung</h2>
        <Badge tone="warn" title="In Produktion abgeschaltet">
          Entwicklermodus
        </Badge>
      </div>
      <p className="text-sm text-[var(--text-muted)]">Microsoft Entra ist hier nicht eingerichtet. Rolle zum Ausprobieren wählen.</p>
      <fieldset className="space-y-2">
        <legend className="ad-label">Rolle</legend>
        {ROLES.map(([value, label, hint]) => (
          <label
            key={value}
            className={`flex cursor-pointer items-start gap-3 border p-3 ${role === value ? 'border-arnold-red bg-arnold-red-tint' : 'border-[var(--border-default)]'}`}
          >
            <input type="radio" name="role" checked={role === value} onChange={() => setRole(value)} className="mt-1 h-[18px] w-[18px] accent-[var(--color-arnold-red)]" />
            <span>
              <span className="block text-[16px] font-semibold text-steel-900">{label}</span>
              <span className="block text-[14px] text-[var(--text-muted)]">{hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="flex flex-wrap gap-2">
        {(['FRD', 'STH', 'BEIDE'] as const).map((s) => (
          <button key={s} type="button" className={`ad-tag ${site === s ? 'ad-tag-active' : ''}`} onClick={() => setSite(s)}>
            {s === 'BEIDE' ? 'Beide Werke' : s}
          </button>
        ))}
      </div>
      <Checkbox label="zusätzlich Systemverwaltung" checked={sysadmin} onChange={setSysadmin} />
      {error ? <ErrorNote error={error} /> : null}
      <Button size="lg" className="w-full" onClick={() => void submit()} disabled={busy}>
        {busy ? 'Anmeldung läuft …' : 'Anmelden'}
      </Button>
    </>
  );
}
